import React from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { DEFAULT_SWITCH_POLICY } from "./switchLink";

// One "party" box in the three-party flow (Client -> Chinook -> Vendors).
function PartyBox({ eyebrow, name, desc, accent }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        flex: 1,
        minWidth: 0,
        textAlign: "center",
        borderColor: accent ? "primary.main" : "divider",
        borderWidth: accent ? 1.5 : 1,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}
      >
        {eyebrow}
      </Typography>
      <Typography variant="subtitle1" fontWeight={700} noWrap>
        {name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {desc}
      </Typography>
    </Paper>
  );
}

function FlowArrow({ label, color }) {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ px: narrow ? 0 : 0.5, py: narrow ? 0.5 : 0, minWidth: narrow ? "auto" : 72 }}
    >
      <ArrowForwardIcon
        fontSize="small"
        sx={{ color, transform: narrow ? "rotate(90deg)" : "none" }}
      />
      <Typography
        variant="caption"
        sx={{ color, fontWeight: 700, whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

function SectionTitle({ n, children }) {
  return (
    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3, mb: 0.5 }}>
      {n} &middot; {children}
    </Typography>
  );
}

/**
 * The business explanation of how a vessel line's cost turns into cash - the
 * non-technical counterpart to the numbers shown throughout this dashboard.
 * Opened from an icon button; the payment-terms and status sections use the
 * project's actual switch policy / compliance data, not made-up figures.
 *
 * @param policy          switchPolicy (switchLink.js) - falls back to
 *                        DEFAULT_SWITCH_POLICY so the dialog never has "no data"
 * @param complianceFlags switchComplianceFlags() result for this project
 * @param anyPaid         whether any real line has actually been paid yet
 * @param demo            true while the page itself is showing demo data
 */
export default function BusinessFlowDialog({
  open,
  onClose,
  policy,
  complianceFlags = [],
  anyPaid = false,
  demo = false,
}) {
  const p = policy || DEFAULT_SWITCH_POLICY;
  const approvalDays = p.approvalDays;
  const clientDays = p.clientLagDays;
  const vendorDays = [p.vendorLagDays.manhour, p.vendorLagDays.material, p.vendorLagDays.subcontract];
  const minVendor = Math.min(...vendorDays);
  const maxVendor = Math.max(...vendorDays);
  const vendorRange = minVendor === maxVendor ? `${minVendor}` : `${minVendor}–${maxVendor}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 1, pb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="overline"
            color="primary.main"
            fontWeight={700}
            sx={{ display: "block", lineHeight: 1.4 }}
          >
            How money flows on this project
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            Collect before you pay
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: 0.5 }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "grid", gap: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          Every dollar of work on this vessel splits into two payments going in
          opposite directions: one the client owes Chinook, one Chinook owes its
          own vendors and people. This is the policy that decides which one
          moves first.
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            p: 1.75,
            borderLeft: 3,
            borderLeftColor: "primary.main",
            bgcolor: "action.hover",
          }}
        >
          <Typography sx={{ fontStyle: "italic" }}>
            The rule, in one line:{" "}
            <Box component="span" sx={{ fontWeight: 700, fontStyle: "normal", color: "primary.main" }}>
              nothing goes out until the money it depends on has come in.
            </Box>
          </Typography>
        </Paper>

        <SectionTitle n={1}>The three parties in every dollar</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Chinook always sits in the middle: cash comes in from the client
          before it goes out to vendors and payroll for that same scope.
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          sx={{ gap: 0 }}
        >
          <PartyBox eyebrow="Upstream" name="Client" desc="owner / owner's engineer" />
          <FlowArrow label="pays" color="success.main" />
          <PartyBox eyebrow="Pass-through" name="Chinook" desc="fabricator / EPC" accent />
          <FlowArrow label="pays" color="warning.main" />
          <PartyBox eyebrow="Downstream" name="Vendors & payroll" desc="materials, subs, shop labor" />
        </Stack>

        <SectionTitle n={2}>The lifecycle of one line</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          When a line's work is done, the client first verifies it. The moment
          they do, the line's switch fires and it is billed that day, at cost
          plus Chinook's markup. From there two waits run back to back, not at
          once: the client's own billing terms, then a second, shorter wait
          before Chinook settles with the vendor or payroll cycle that earned
          that cost. Labour lines go through this in packets of{" "}
          {p.packetDays} days, so payroll is covered as the work progresses
          rather than only at the end.
        </Typography>
        <Stepper alternativeLabel sx={{ mt: 1 }}>
          <Step completed>
            <StepLabel optional={<Typography variant="caption" color="text.secondary">Day 0</Typography>}>
              Work done
            </StepLabel>
          </Step>
          <Step completed>
            <StepLabel optional={<Typography variant="caption" color="text.secondary">{`Day ${approvalDays} · switch fires`}</Typography>}>
              Client approves &amp; is billed
            </StepLabel>
          </Step>
          <Step completed>
            <StepLabel optional={<Typography variant="caption" color="text.secondary">{`Day ${approvalDays + clientDays} · progress billing terms`}</Typography>}>
              Client pays
            </StepLabel>
          </Step>
          <Step completed>
            <StepLabel optional={<Typography variant="caption" color="text.secondary">{`+${vendorRange} days after that`}</Typography>}>
              Vendor / payroll paid
            </StepLabel>
          </Step>
        </Stepper>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          The vendor/payroll wait only starts once the client has actually
          paid — it is a second, sequential wait, not a parallel one.
        </Typography>

        <SectionTitle n={3}>Why the order matters</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Add up everything collected from the client so far, and everything
          paid out to vendors and payroll so far. The gap between those two
          running totals is Chinook's cash cushion on this project — and the
          order above is what keeps it from ever going below zero.
        </Typography>
        <Stack spacing={1}>
          <Alert
            severity="error"
            variant="outlined"
            icon={<TrendingDownIcon fontSize="small" />}
          >
            <b>Without sequencing</b> &mdash; pay the vendor as soon as the work
            finishes, without waiting on the client, and the cushion can run
            negative: Chinook is temporarily funding the client's project with
            its own cash.
          </Alert>
          <Alert
            severity="success"
            variant="outlined"
            icon={<TrendingUpIcon fontSize="small" />}
          >
            <b>With the switch (used on this project)</b> &mdash; the vendor is
            paid only after the client has paid, so nothing is ever paid out
            before it has already been collected.
          </Alert>
        </Stack>

        <SectionTitle n={4}>Payment terms on this project</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The vendor/payroll wait is not one number &mdash; it is set per cost
          type, matched to how that party is actually paid. These are the terms
          currently configured here.
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cost type</TableCell>
              <TableCell>Paid on</TableCell>
              <TableCell align="right">Wait, after client pays</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Payroll <Typography component="span" variant="caption" color="text.secondary">— shop labor</Typography></TableCell>
              <TableCell>Fixed payroll cycle</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{p.vendorLagDays.manhour} days</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Materials <Typography component="span" variant="caption" color="text.secondary">— vendor invoices</Typography></TableCell>
              <TableCell>Standard vendor terms</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{p.vendorLagDays.material} days</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Subcontract</TableCell>
              <TableCell>Pay application terms</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{p.vendorLagDays.subcontract} days</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ color: "text.secondary" }}>
                Client approval <Typography component="span" variant="caption">(for reference)</Typography>
              </TableCell>
              <TableCell sx={{ color: "text.secondary" }}>Work done → switch fires (+1 round per resubmittal)</TableCell>
              <TableCell align="right" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>{approvalDays} days</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ color: "text.secondary" }}>
                Client billing <Typography component="span" variant="caption">(for reference)</Typography>
              </TableCell>
              <TableCell sx={{ color: "text.secondary" }}>Progress billing / owner terms, after the switch</TableCell>
              <TableCell align="right" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>{clientDays} days</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Every vendor/payroll wait above is measured from the client's payment
          date, not from the day the work finished. Lines are billed at cost
          plus a markup of {p.markupPct.manhour}% (payroll),{" "}
          {p.markupPct.material}% (materials) and {p.markupPct.subcontract}%
          (subcontract). Change these under &ldquo;Cash flow policy&rdquo; in
          the Cash flow panel.
        </Typography>

        <SectionTitle n={5}>What breaks the discipline</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The one failure mode that matters is a vendor or payroll payment going
          out before the client has actually paid for that same scope of work
          &mdash; that would mean Chinook is lending its own cash to cover the
          client's share, which is exactly what this sequencing exists to
          prevent.
        </Typography>
        {demo ? (
          <Alert severity="info" variant="outlined">
            This page is showing demo data, so there is nothing real to check
            yet — once an estimate is saved for this revision, this section
            reflects this project's actual payments.
          </Alert>
        ) : complianceFlags.length > 0 ? (
          <Alert severity="error" variant="outlined">
            On this project right now: {complianceFlags.length}{" "}
            {complianceFlags.length === 1 ? "line was" : "lines were"} paid
            before any of its switches fired &mdash;{" "}
            {complianceFlags.map((f) => f.displayCode).join(", ")}.
          </Alert>
        ) : anyPaid ? (
          <Alert severity="success" variant="outlined">
            On this project right now: every paid line had its switch fire
            first &mdash; nothing has run ahead of the policy.
          </Alert>
        ) : (
          <Alert severity="info" variant="outlined">
            Nothing has been paid on this project yet, so there is nothing to
            check against the policy so far.
          </Alert>
        )}

        <Divider sx={{ mt: 3, mb: 1.5 }} />
        <Typography variant="caption" color="text.secondary">
          Applied consistently, line by line, this sequence does one simple
          thing: it makes sure Chinook is never further out of pocket on a
          piece of work than the client already is.
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
